import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyReferredUsers } from '../../Api/CoreApi';
import {
  FaGift, FaUsers, FaSearch, FaSpinner, FaArrowLeft, FaArrowRight,
  FaCrown, FaCheckCircle, FaCopy, FaShareAlt, FaWhatsapp, FaEye,
  FaCode, FaLink, FaCalendarAlt, FaEnvelope, FaPhone, FaMapMarkerAlt,
} from 'react-icons/fa';
import { message } from 'antd';
import {BASE_API} from "../../Api/Api"

const MyReferrals = () => {
  const navigate = useNavigate();
  const BASE_URL = BASE_API;
  const [loading, setLoading] = useState(true);
  const [referredUsers, setReferredUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [myCode, setMyCode] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [copied, setCopied] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchReferredUsers();
  }, []);

  const fetchReferredUsers = async () => {
    setLoading(true);
    try {
      const data = await getMyReferredUsers();
      setReferredUsers(data.referred_users || []);
      setStats(data.stats || {});
      setMyCode(data.my_referral_code || '');
    } catch (err) {
      console.error("Error:", err);
      message.error("Failed to load referred users");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(myCode).then(() => {
      setCopied(true);
      message.success("✅ Code copied!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleWhatsApp = () => {
    const text = `🎉 Join me on Rishtey!\n\nUse my referral code: *${myCode}*`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  // Filter Data
  const filteredData = referredUsers.filter(user => {
    const matchesSearch = 
      user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.contact?.includes(searchTerm) ||
      user.profile_id?.toLowerCase().includes(searchTerm.toLowerCase());

    if (filterBy === 'premium') return matchesSearch && user.is_premium;
    if (filterBy === 'active') return matchesSearch && user.is_active;
    if (filterBy === 'code') return matchesSearch && user.added_method === 'referral_code';
    if (filterBy === 'link') return matchesSearch && user.added_method === 'referral_link';
    return matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredData.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const getMethodBadge = (method) => {
    if (method === 'referral_link') {
      return { icon: <FaLink />, label: 'Via Link', bg: '#fffbeb', color: '#f59e0b', border: '#fde68a' };
    }
    return { icon: <FaCode />, label: 'Via Code', bg: '#f5f3ff', color: '#8b5cf6', border: '#ddd6fe' };
  };

  const statsArr = [
    { label: 'Total Referred', value: stats.total || 0, icon: FaUsers, gradient: 'linear-gradient(135deg, #ff0055, #ff4d88)' },
    { label: 'Premium Users', value: stats.premium || 0, icon: FaCrown, gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)' },
    { label: 'Active Users', value: stats.active || 0, icon: FaCheckCircle, gradient: 'linear-gradient(135deg, #10b981, #34d399)' },
    { label: 'Via Code', value: stats.via_code || 0, icon: FaCode, gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' },
  ];

  if (loading) {
    return (
      <>
        <style>{`@keyframes mrSpin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 16, background: '#fff' }}>
          <FaSpinner size={48} style={{ color: '#ff0055', animation: 'mrSpin 1s linear infinite' }} />
          <p style={{ color: '#888', fontSize: 14 }}>🎁 Loading your referrals...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
        * { font-family: 'Poppins', sans-serif !important; box-sizing: border-box; margin: 0; }
        @keyframes mrSpin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }

        .mr-page { background: #fff; min-height: 100vh; padding: 20px 16px; }
        .mr-wrap { max-width: 1280px; margin: 0 auto; }

        .mr-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 22px; flex-wrap: wrap; gap: 12px; padding: 22px 26px; border-radius: 20px; background: linear-gradient(135deg, #fff0f3, #ffe4ec); border: 2px solid #ffe4ec; }
        .mr-header-left h1 { font-size: 24px; font-weight: 800; color: #1a0a10; margin: 0; display: flex; align-items: center; gap: 10px; }
        .mr-header-left h1 svg { color: #ff0055; }
        .mr-header-left p { color: #888; font-size: 13.5px; margin: 4px 0 0; }
        
        .mr-code-box { background: #fff; padding: 12px 20px; border-radius: 14px; border: 2px solid #ffd3df; display: flex; align-items: center; gap: 12px; }
        .mr-code-label { font-size: 10px; color: #ff4d88; font-weight: 700; text-transform: uppercase; margin: 0; }
        .mr-code-value { font-size: 22px; font-weight: 900; color: #ff0055; margin: 2px 0 0; letter-spacing: 2px; }
        .mr-code-btns { display: flex; gap: 6px; margin-left: 8px; }
        .mr-code-btn { padding: 8px 12px; border: none; border-radius: 8px; cursor: pointer; font-size: 11px; font-weight: 700; display: flex; align-items: center; gap: 4px; transition: all 0.3s; }
        .mr-code-copy { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; }
        .mr-code-wa { background: linear-gradient(135deg, #25d366, #128c7e); color: #fff; }
        .mr-code-btn:hover { transform: translateY(-2px); }

        .mr-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 22px; }
        .mr-stat-card { background: #fff; border: 2px solid #ffe4ec; border-radius: 16px; padding: 16px; display: flex; align-items: center; gap: 12px; transition: all 0.3s; box-shadow: 0 4px 14px rgba(255,0,85,0.04); }
        .mr-stat-card:hover { transform: translateY(-3px); border-color: #ffb3c8; box-shadow: 0 10px 24px rgba(255,0,85,0.1); }
        .mr-stat-icon { width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 20px; color: #fff; flex-shrink: 0; }
        .mr-stat-value { font-size: 22px; font-weight: 800; color: #1a0a10; margin: 0; }
        .mr-stat-label { font-size: 12px; color: #888; margin: 2px 0 0; font-weight: 500; }

        .mr-filters { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; flex-wrap: wrap; gap: 12px; padding: 14px 16px; background: #fff; border-radius: 14px; border: 2px solid #ffe4ec; }
        .mr-search-wrap { position: relative; flex: 1; min-width: 220px; }
        .mr-search { width: 100%; padding: 10px 14px 10px 40px; border: 1.5px solid #ffe4ec; border-radius: 12px; font-size: 13px; outline: none; background: #fff; transition: all 0.3s; }
        .mr-search:focus { border-color: #ff0055; box-shadow: 0 0 0 3px rgba(255,0,85,0.08); }
        .mr-search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #ff4d88; }

        .mr-filter-group { display: flex; gap: 5px; padding: 4px; background: #fff0f3; border: 1.5px solid #ffe4ec; border-radius: 12px; flex-wrap: wrap; }
        .mr-filter-btn { padding: 7px 14px; border-radius: 9px; border: none; background: transparent; cursor: pointer; font-size: 12px; color: #888; font-weight: 600; transition: all 0.25s; }
        .mr-filter-btn:hover { color: #ff0055; }
        .mr-filter-btn.active { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; box-shadow: 0 4px 12px rgba(255,0,85,0.18); }

        .mr-table-card { background: #fff; border-radius: 18px; border: 2px solid #ffe4ec; overflow: hidden; box-shadow: 0 8px 24px rgba(255,0,85,0.05); }
        .mr-table-wrap { overflow-x: auto; }
        .mr-table { width: 100%; border-collapse: collapse; min-width: 900px; }
        .mr-table thead { background: linear-gradient(135deg, #fff0f3, #ffe4ec); border-bottom: 2px solid #ffd3df; }
        .mr-table th { padding: 14px 16px; text-align: left; font-size: 11.5px; font-weight: 700; color: #c2185b; text-transform: uppercase; letter-spacing: 0.5px; }
        .mr-table tr { border-bottom: 1px solid #fff0f3; transition: background 0.25s; }
        .mr-table tbody tr:hover { background: #fffafc; }
        .mr-table td { padding: 14px 16px; vertical-align: middle; }

        .mr-profile-cell { display: flex; align-items: center; gap: 12px; }
        .mr-profile-img { width: 44px; height: 44px; border-radius: 50%; border: 2.5px solid #ff0055; box-shadow: 0 4px 12px rgba(255,0,85,0.18); object-fit: cover; }
        .mr-profile-name { font-size: 14px; font-weight: 700; color: #1a0a10; margin: 0; }
        .mr-profile-id { font-size: 11.5px; color: #ff4d88; margin: 2px 0 0; font-weight: 600; font-family: 'Courier New', monospace; }

        .mr-td-main { font-size: 13px; color: #444; font-weight: 600; }
        .mr-td-sub { font-size: 11px; color: #999; margin-top: 2px; display: flex; align-items: center; gap: 4px; }

        .mr-method-badge { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; border: 1.5px solid; }

        .mr-plan-badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 10.5px; font-weight: 700; }
        .mr-plan-premium { background: linear-gradient(135deg, #fef3c7, #fde68a); color: #92400e; border: 1px solid #fbbf24; }
        .mr-plan-free { background: #f3f4f6; color: #6b7280; border: 1px solid #d1d5db; }

        .mr-status-badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 999px; font-size: 10.5px; font-weight: 700; }
        .mr-status-active { background: #d1fae5; color: #065f46; }
        .mr-status-inactive { background: #fee2e2; color: #991b1b; }

        .mr-view-btn { width: 32px; height: 32px; border: none; border-radius: 9px; cursor: pointer; background: linear-gradient(135deg, #fff0f3, #ffe4ec); color: #ff0055; display: flex; align-items: center; justify-content: center; transition: all 0.25s; }
        .mr-view-btn:hover { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; transform: translateY(-2px); }

        .mr-empty { padding: 60px 24px; text-align: center; }
        .mr-empty-icon { width: 80px; height: 80px; border-radius: 50%; background: linear-gradient(135deg, #fff0f3, #ffe4ec); display: flex; align-items: center; justify-content: center; margin: 0 auto 18px; font-size: 32px; color: #ff0055; }
        .mr-empty-title { font-size: 20px; font-weight: 800; color: #c2185b; margin: 0 0 8px; }
        .mr-empty-sub { color: #888; margin: 0 0 20px; font-size: 13.5px; }
        .mr-empty-btn { padding: 11px 28px; border-radius: 999px; border: none; background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; box-shadow: 0 8px 22px rgba(255,0,85,0.22); transition: all 0.3s; }
        .mr-empty-btn:hover { transform: translateY(-2px); }

        .mr-pagination { display: flex; justify-content: space-between; align-items: center; margin-top: 20px; padding: 16px 4px; flex-wrap: wrap; gap: 10px; }
        .mr-page-info { font-size: 13px; color: #888; }
        .mr-page-info strong { color: #ff0055; font-weight: 800; }
        .mr-page-btns { display: flex; gap: 6px; flex-wrap: wrap; }
        .mr-page-btn { min-width: 34px; height: 34px; border-radius: 9px; border: 1.5px solid #ffe4ec; background: #fff; color: #888; cursor: pointer; font-weight: 600; font-size: 13px; display: flex; align-items: center; justify-content: center; transition: all 0.25s; padding: 0 10px; }
        .mr-page-btn:hover:not(:disabled) { border-color: #ff0055; color: #ff0055; }
        .mr-page-btn.active { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; border-color: #ff0055; box-shadow: 0 4px 12px rgba(255,0,85,0.2); }
        .mr-page-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        @media (max-width: 1024px) {
          .mr-stats-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 768px) {
          .mr-header { flex-direction: column; align-items: stretch; }
          .mr-code-box { flex-direction: column; align-items: flex-start; }
        }
      `}</style>

      <div className="mr-page">
        <div className="mr-wrap">

          {/* Header */}
          <div className="mr-header">
            <div className="mr-header-left">
              <h1><FaGift /> My Referrals</h1>
              <p>Users who registered using your referral code</p>
            </div>

            {myCode && (
              <div className="mr-code-box">
                <div>
                  <p className="mr-code-label">Your Code</p>
                  <p className="mr-code-value">{myCode}</p>
                </div>
                <div className="mr-code-btns">
                  <button className="mr-code-btn mr-code-copy" onClick={handleCopy}>
                    <FaCopy size={11} /> {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button className="mr-code-btn mr-code-wa" onClick={handleWhatsApp}>
                    <FaWhatsapp size={12} /> Share
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="mr-stats-grid">
            {statsArr.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="mr-stat-card">
                  <div className="mr-stat-icon" style={{ background: s.gradient }}>
                    <Icon />
                  </div>
                  <div>
                    <p className="mr-stat-value">{s.value}</p>
                    <p className="mr-stat-label">{s.label}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Filters */}
          <div className="mr-filters">
            <div className="mr-search-wrap">
              <FaSearch className="mr-search-icon" />
              <input
                type="text"
                placeholder="Search by name, ID, email, mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mr-search"
              />
            </div>

            <div className="mr-filter-group">
              <button className={`mr-filter-btn ${filterBy === 'all' ? 'active' : ''}`} onClick={() => setFilterBy('all')}>
                All
              </button>
              <button className={`mr-filter-btn ${filterBy === 'active' ? 'active' : ''}`} onClick={() => setFilterBy('active')}>
                Active
              </button>
              <button className={`mr-filter-btn ${filterBy === 'premium' ? 'active' : ''}`} onClick={() => setFilterBy('premium')}>
                Premium
              </button>
              <button className={`mr-filter-btn ${filterBy === 'code' ? 'active' : ''}`} onClick={() => setFilterBy('code')}>
                Via Code
              </button>
              <button className={`mr-filter-btn ${filterBy === 'link' ? 'active' : ''}`} onClick={() => setFilterBy('link')}>
                Via Link
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="mr-table-card">
            {currentItems.length > 0 ? (
              <div className="mr-table-wrap">
                <table className="mr-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Contact</th>
                      <th>Location</th>
                      <th>Method</th>
                      <th>Plan</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((user) => {
                      const methodBadge = getMethodBadge(user.added_method);
                      const imageSrc = user.profile_pic
                        ? user.profile_pic
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(user.first_name || user.username)}&background=ff0055&color=fff&size=128`;

                      return (
                        <tr key={user.id}>
                          <td>
                            <div className="mr-profile-cell">
                              <img
                                src={imageSrc}
                                alt={user.first_name}
                                className="mr-profile-img"
                                onError={(e) => {
                                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.first_name || 'U')}&background=ff0055&color=fff&size=128`;
                                }}
                              />
                              <div>
                                <p className="mr-profile-name">{user.first_name || user.username}</p>
                                <p className="mr-profile-id">{user.profile_id || 'N/A'}</p>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="mr-td-main">
                              <FaPhone size={10} style={{ marginRight: 4, color: '#ff4d88' }} />
                              {user.contact || 'N/A'}
                            </div>
                            <div className="mr-td-sub">
                              <FaEnvelope size={9} />
                              {user.email || 'No email'}
                            </div>
                          </td>
                          <td>
                            <div className="mr-td-main">
                              {user.city || user.state ? (
                                <>
                                  <FaMapMarkerAlt size={10} style={{ marginRight: 4, color: '#ff4d88' }} />
                                  {user.city || ''}
                                </>
                              ) : 'N/A'}
                            </div>
                            <div className="mr-td-sub">{user.state || ''}</div>
                          </td>
                          <td>
                            <span
                              className="mr-method-badge"
                              style={{
                                background: methodBadge.bg,
                                color: methodBadge.color,
                                borderColor: methodBadge.border,
                              }}
                            >
                              {methodBadge.icon} {methodBadge.label}
                            </span>
                          </td>
                          <td>
                            <span className={`mr-plan-badge ${user.is_premium ? 'mr-plan-premium' : 'mr-plan-free'}`}>
                              {user.is_premium ? '⭐ Premium' : 'Free'}
                            </span>
                          </td>
                          <td>
                            <span className={`mr-status-badge ${user.is_active ? 'mr-status-active' : 'mr-status-inactive'}`}>
                              {user.is_active ? '● Active' : '○ Inactive'}
                            </span>
                          </td>
                          <td>
                            <div className="mr-td-main">
                              <FaCalendarAlt size={10} style={{ marginRight: 4, color: '#ff4d88' }} />
                              {formatDate(user.date_joined)}
                            </div>
                          </td>
                          <td>
                            <button
                              className="mr-view-btn"
                              onClick={() => navigate(`/user-profile/${user.id}`)}
                              title="View Profile"
                            >
                              <FaEye size={12} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mr-empty">
                <div className="mr-empty-icon">
                  <FaGift />
                </div>
                <h3 className="mr-empty-title">
                  {referredUsers.length === 0 ? 'No Referrals Yet' : 'No Match Found'}
                </h3>
                <p className="mr-empty-sub">
                  {referredUsers.length === 0
                    ? "Share your referral code and start earning!"
                    : "Try changing your search or filters"}
                </p>
                {referredUsers.length === 0 && (
                  <button className="mr-empty-btn" onClick={() => navigate('/dashboard')}>
                    🎁 Back to Dashboard
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredData.length > 0 && totalPages > 1 && (
            <div className="mr-pagination">
              <div className="mr-page-info">
                Showing <strong>{startIndex + 1}-{Math.min(endIndex, filteredData.length)}</strong> of <strong>{filteredData.length}</strong> referrals
              </div>
              <div className="mr-page-btns">
                <button
                  className="mr-page-btn"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <FaArrowLeft size={10} />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    className={`mr-page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                    onClick={() => goToPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className="mr-page-btn"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <FaArrowRight size={10} />
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default MyReferrals;